import {RegFormKeysBasedOnAccountT, getRegistrationSchema} from '@tnc/shared/schemas';
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {z} from 'zod';

import {brandFormFields, brandFormSteps} from '@/lib/forms/brand-form-config';
import {FormIds} from '@/lib/forms/form-ids';
import {getTermsFormFields, termsFormSteps} from '@/lib/forms/terms-form-config';

import {useAuth} from '@/providers/components/authProvider';
import {useAppForm} from '@/providers/components/formProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {Modal, ModalRefT} from '@/components/Modal';
import FormFields from '@/components/form/FormFields';
import FormGlobalError from '@/components/form/FormGlobalError';
import FormStepsButtons from '@/components/form/FormStepsButtons';
import FormSubmitButton from '@/components/form/FormSubmitButton';

// Use the registration schema type
type BrandRegistrationFormT = RegFormKeysBasedOnAccountT<'brand'> & {
  brand_same_as_speculator_email?: boolean;
  brand_same_as_speculator_address?: boolean;
  speculator_email?: string;
};

// Use the registration schema helper
const brandSchema = getRegistrationSchema('brand').extend({
  brand_same_as_speculator_email: z.boolean().optional(),
  brand_same_as_speculator_address: z.boolean().optional(),
  speculator_email: z.string(),
});

export type RegisterBrandRefT = {
  show: () => void;
  hide: () => void;
};

const RegisterBrand = forwardRef<RegisterBrandRefT>((_, ref) => {
  const {
    userAccounts: {speculator},
    refreshUserAccounts,
    handleRegister,
  } = useAuth();
  const {toast} = useNotifications();

  // Refs
  const registerBrandModalRef = useRef<ModalRefT | null>(null);

  // Expose show/hide methods to parent components
  useImperativeHandle(ref, () => ({
    show: () => registerBrandModalRef.current?.show(),
    hide: () => registerBrandModalRef.current?.hide(),
  }));

  // Initialize form with config
  const {formConfig, setGlobalError, setLoading} = useAppForm<BrandRegistrationFormT>({
    formId: FormIds.BRAND_REGISTRATION,
    config: {
      fields: [...brandFormFields, ...getTermsFormFields('brand', true)],
      steps: (() => {
        const stepsArr = [...brandFormSteps];
        const stepCount = brandFormSteps.length;

        // Add terms steps last
        stepsArr.push(
          ...termsFormSteps.map((step, index) => ({
            ...step,
            step: index + stepCount,
            // Only include brand terms and terms_accepted
            fields: ['brand_terms', 'terms_accepted'],
          }))
        );

        return stepsArr;
      })(),
      defaultValues: {
        speculator_email: speculator?.speculator_email || undefined,
        brand_same_as_speculator_address: false,
        terms_accepted: false,
      },
      zodSchema: brandSchema,
    },
  });

  const {handleSubmit, reset, watch, setValue} = formConfig;

  // Watch for changes to the same as speculator address checkbox
  useEffect(() => {
    const subscription = watch((value: BrandRegistrationFormT, {name}: {name: keyof BrandRegistrationFormT}) => {
      if (name === 'brand_same_as_speculator_address') {
        if (value.brand_same_as_speculator_address) {
          setValue('brand_address', speculator?.speculator_address || '', {shouldValidate: true});
          setValue('brand_address2', speculator?.speculator_address2 || '', {shouldValidate: true});
          setValue('brand_city', speculator?.speculator_city || '', {shouldValidate: true});
          setValue('brand_state', speculator?.speculator_state || '', {shouldValidate: true});
          setValue('brand_postal_code', speculator?.speculator_postal_code || '', {shouldValidate: true});
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, speculator]);

  // Submit handler
  const onSubmit = async (data: BrandRegistrationFormT) => {
    try {
      setLoading(true);

      // First validate with the UI schema that includes helper fields
      const uiValidationResult = brandSchema.safeParse(data);

      if (!uiValidationResult.success) {
        window.scrollTo(0, 0);
        console.error('Form validation failed:', uiValidationResult.error);
        setGlobalError('Please fix the validation errors below.');

        uiValidationResult.error.issues.forEach(issue => {
          const path = issue.path.join('.');
          formConfig.setError(path as keyof BrandRegistrationFormT, {
            type: 'validation',
            message: issue.message,
          });
        });

        return;
      }

      // Now clean the data using the base schema to remove UI-only fields
      const baseSchema = getRegistrationSchema('brand');
      const cleanResult = baseSchema.safeParse(data);

      if (!cleanResult.success) {
        window.scrollTo(0, 0);
        console.error('Data cleaning failed:', cleanResult.error);
        setGlobalError('Internal validation error. Please try again.');
        return;
      }

      // Send only the cleaned data to the backend
      await handleRegister<typeof cleanResult.data>({
        data: cleanResult.data,
        accountType: 'brand',
      });
      toast.success('Brand registered successfully!');
      registerBrandModalRef.current?.hide();
    } catch (error) {
      // Handle validation errors from the server
      if (
        error instanceof Error &&
        error.cause &&
        typeof error.cause === 'object' &&
        'validationErrors' in error.cause &&
        'message' in error.cause
      ) {
        const {validationErrors, message} = error.cause as {
          validationErrors: Record<string, string[]>;
          message: string;
        };

        window.scrollTo(0, 0);
        setGlobalError(message);

        // Set field errors
        Object.entries(validationErrors).forEach(([field, messages]) => {
          formConfig.setError(field as keyof BrandRegistrationFormT, {
            type: 'server',
            message: messages[0],
          });
        });

        return;
      }

      // Handle other types of errors
      setGlobalError(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
      toast.error('Failed to register brand');
    } finally {
      await refreshUserAccounts();
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    reset();
  };

  return (
    <Modal
      title="Register a Brand"
      ref={registerBrandModalRef}
      clickToClose={false}
      onClose={handleClose}
      closeButtonLabel="Cancel"
    >
      <FormProvider {...formConfig}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex max-h-[calc(100vh-200px)] flex-col gap-6 overflow-y-auto px-6"
          noValidate
        >
          <FormGlobalError formId={FormIds.BRAND_REGISTRATION} />
          <FormStepsButtons formId={FormIds.BRAND_REGISTRATION} />
          <FormFields formId={FormIds.BRAND_REGISTRATION} />
          <FormSubmitButton formId={FormIds.BRAND_REGISTRATION} />
        </form>
      </FormProvider>
    </Modal>
  );
});

RegisterBrand.displayName = 'RegisterBrand';

export default RegisterBrand;
